import React, { useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { useNavigate } from 'react-router-dom'
import axios from '../axiosInstance'
import Sidebar from '../components/sidebar'
import Topbar from '../components/topbar'
import { errorMessage, paymentMethodFormat } from '../hooks/UserLogs'
import { convertToDate, formatQueryDate, getRecentCalendarMonthRange, isValidDateRange } from '../utils/date'
import { downloadBlob, getTimestampedFilename } from '../utils/download'
const getInitialDates = () => {
  const { begin, end } = getRecentCalendarMonthRange()
  return {
    begin: formatQueryDate(begin),
    end: formatQueryDate(end),
  }
}

const formatNumber = (value) => Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })

export default function BillerGroup () {
  const initialDates = getInitialDates()
  const [beginDate, setBeginDate] = useState(initialDates.begin)
  const [endDate, setEndDate] = useState(initialDates.end)
  const [groups, setGroups] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [errMsg, setErrMsg] = useState("Une erreur s'est produite.")
  const [showError, setShowError] = useState(false)
  const navigate = useNavigate()
  const bUrl = process.env.REACT_APP_B_URL

  const showRequestError = (err) => {
    if (err?.response?.status === 401) {
      navigate('/backoffice/login')
      return
    }
    if (err?.response?.status === 403) {
      setErrMsg("Vous n'êtes pas autorisé à faire cette opération.")
    } else {
      setErrMsg("Une erreur s'est produite.")
    }
    setShowError(true)
  }

  const validateDates = (requestedBeginDate = beginDate, requestedEndDate = endDate) => {
    if (isValidDateRange(requestedBeginDate, requestedEndDate)) return true
    setErrMsg("La date de fin doit être postérieure ou égale à la date de début.")
    setShowError(true)
    return false
  }

  const getGroups = async (requestedBeginDate = beginDate, requestedEndDate = endDate) => {
    if (!validateDates(requestedBeginDate, requestedEndDate)) return
    setIsLoading(true)
    try {
      const response = await axios.get(bUrl + '/biller-invoice/billerGroup', {
        params: {
          beginDate: convertToDate(new Date(requestedBeginDate)),
          endDate: convertToDate(new Date(requestedEndDate)),
        },
      })
      if (response?.data?.isError) {
        setErrMsg(errorMessage(response.data.code) || response.data.msg || "Une erreur s'est produite.")
        setShowError(true)
        return
      }
      setGroups(response?.data?.body || [])
    } catch (err) {
      showRequestError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadGroups = async () => {
    if (!validateDates()) return
    try {
      const response = await axios.get(bUrl + '/biller-invoice/downloadGroup', {
        params: {
          beginDate: convertToDate(new Date(beginDate)),
          endDate: convertToDate(new Date(endDate)),
        },
        responseType: 'blob',
      })
      downloadBlob(response.data, getTimestampedFilename('sales-total-report'))
    } catch (err) {
      showRequestError(err)
    }
  }

  const resetFilters = () => {
    const dates = getInitialDates()
    setBeginDate(dates.begin)
    setEndDate(dates.end)
    getGroups(dates.begin, dates.end)
  }

  useEffect(() => {
    if (sessionStorage.getItem('name') === '' || sessionStorage.getItem('name') === null) {
      navigate('/backoffice/login')
      return
    }
    getGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="App list-page biller-group-page">
      <Sidebar />
      <section className="home">
        <div className="row">
          <div className="d-grid col-12 gap-3">
            <div className="table-content mt-2 p-2 text d-flex justify-content-between align-items-start">
              <div className="list-page-header"><p className="fw-bold mb-0">Rapport du total des ventes</p></div>
              <Topbar />
            </div>

            <div className="d-grid pb-2 gap-3">
            <div className="list-filter-card">
              <div className="list-filter-toolbar">
                <div className="list-filter-fields">
                  <div className="list-field text">
                    <label htmlFor="groupBeginDate">Début</label>
                      <input id="groupBeginDate" type="date" value={beginDate} onChange={(e) => { setBeginDate(e.target.value); getGroups(e.target.value, endDate) }} />
                  </div>
                  <div className="list-field text">
                    <label htmlFor="groupEndDate">Fin</label>
                      <input id="groupEndDate" type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); getGroups(beginDate, e.target.value) }} />
                  </div>
                </div>
                <div className="list-actions-group">
                  <button type="button" className="list-btn-ghost" onClick={resetFilters} disabled={isLoading}>
                    <i className="bx bx-reset" /> Réinitialiser
                  </button>
                  <button type="button" className="list-btn-primary" onClick={downloadGroups}>
                    <i className="bx bx-download" /> Exporter
                  </button>
                </div>
              </div>
            </div>

            <div className="list-table-wrap p-3">
              <div className="table-responsive">
                {isLoading ? (
                  <div className="loading"><img src="../Custom.svg" alt="Chargement" /></div>
                ) : (
                  <table className="table list-table text mb-0">
                    <thead>
                      <tr>
                        <th scope="col">Nº</th>
                        <th scope="col">Plateforme</th>
                        <th scope="col">Mode de paiement</th>
                        <th scope="col">Montant total</th>
                        <th scope="col">Total kWh</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.length > 0 ? groups.map((group, index) => (
                        <tr key={`${group.manufacturer}-${group.paymentMode}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{group.manufacturer || '-'}</td>
                          <td>{group.paymentMode ? paymentMethodFormat(group.paymentMode) : '-'}</td>
                          <td>{formatNumber(group.payTotal)}</td>
                          <td>{formatNumber(group.kwhTotal)}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan="5" className="list-empty">Aucune donnée disponible pour cette période.</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      <Modal show={showError} onHide={() => setShowError(false)}>
        <Modal.Header closeButton className="bg-danger">
          <Modal.Title>ERREUR</Modal.Title>
        </Modal.Header>
        <Modal.Body><h5 className="text-danger text-center">{errMsg}</h5></Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowError(false)}>Fermer</Button></Modal.Footer>
      </Modal>
    </div>
  )
}
